//@flow
/**
 * Main thread uses this to communicate with worker
 */
import { type EntityImportWorker } from './createEntityImportWorker';
import ENTITY_IMPORT_TASK_TYPE_ENUM, {
    type MsgStatusEnumType,
    type ImportTaskTypeEnumType
} from 'common/entity/generic/EntityImportContainer/EntityImportTaskTypesConstants';
import WorkerController from 'common/utils/WorkerController';

type ErrorInterceptorTypes = ({
    statusCode: number,
    message: string,
    onRetry?: () => void,
    retryMetaData?: any,
    timeout?: number
}) => void;

export type SendMsgPayloadType = {
    type: ImportTaskTypeEnumType
};

class EntityImportWorkerController extends WorkerController<
    SendMsgPayloadType,
    MsgStatusEnumType
> {
    worker: EntityImportWorker;
    interceptError: ErrorInterceptorTypes;
    lastToastMessage: string;
    constructor({ ImportWorker }: { ImportWorker: Class<EntityImportWorker> }) {
        super();
        const worker = new ImportWorker();
        worker.onmessage = this._handelMsgFromWorker;
        this.worker = worker;
    }

    _sendMsg = (payload: SendMsgPayloadType) =>
        super.sendMsg(payload, this.worker);

    _handleError = msg => {
        const { err } = msg.data;
        if (Array.isArray(err)) {
            err.forEach(err => this._handleError({ data: { err } }));
        }
        if (
            err.message &&
            err.message === this.lastToastMessage &&
            err.errType === 'ErrorWithAutoAcknowldge'
        ) {
            // 'ErrorWithAutoAcknowldge' same messages will be ignored
            return;
        }
        if (err.errType === 'ErrorWithAutoAcknowldge') {
            this.lastToastMessage = err.message;
            this.interceptError({
                message: err.message,
                timeout: err.autoAcknowledgeTimeout || 10000,
                statusCode: err.statusCode
            });
            this.handleMsg(msg);
        } else if (err.errType === 'ApiError' && err.statusCode === 401) {
            this.interceptError({
                statusCode: err.statusCode,
                message: err.message
            });
        } else if (err.errType === 'ApiError') {
            const onRetry = () => {
                this.retryFailedActionById({
                    failedTaskId: err.id
                });
            };
            this.interceptError({
                message: err.message,
                statusCode: err.statusCode,
                timeout: 0,
                onRetry
            });
        } else {
            this.handleMsg(msg);
        }
        //default handler
    };

    // Intercepting worker messages
    _handelMsgFromWorker = msg => {
        const { err, payload } = msg.data;
        if (!payload && err) {
            this._handleError(msg);
        } else {
            // for success and uncaught errors
            this.handleMsg(msg);
        }
    };

    // initalizing worker
    build = (
        payload: {
            dashboardEndPoint: string,
            authHeader: { key: string, value: string },
            entityNameId: string,
            clientId: string,
            interceptError: ErrorInterceptorTypes
        } = {}
    ) => {
        const { interceptError, ...rest } = payload;
        this.interceptError = interceptError;
        return this._sendMsg({
            ...rest,
            type: ENTITY_IMPORT_TASK_TYPE_ENUM.REGISTER_HELPERS
        });
    };

    parseCSV = ({
        file,
        config
    }: {
        file: File
    }): ?Promise<Function, Function> => {
        const reqBody = {
            file,
            config,
            type: ENTITY_IMPORT_TASK_TYPE_ENUM.PARSE_CSV
        };
        return this._sendMsg(reqBody);
    };

    prepareTableData = () => {
        const reqBody = {
            type: ENTITY_IMPORT_TASK_TYPE_ENUM.PREPARE_TABLE_DATA
        };
        return this._sendMsg(reqBody);
    };

    validateEntityIds = () => {
        const reqBody = {
            type: ENTITY_IMPORT_TASK_TYPE_ENUM.VALIDATE_ENTITY_IDS
        };
        return this._sendMsg(reqBody);
    };

    getIdValidationTables = () => {
        const reqBody = {
            type: ENTITY_IMPORT_TASK_TYPE_ENUM.GENERATE_ID_VALIDATION_TABLE_DATA
        };
        return this._sendMsg(reqBody);
    };

    getOtherValidationsTableAndSetValidationErrors = ({
        ignoredRowIds,
        verifiedRowIds,
        doNotAllowNewEntry
    }) => {
        const reqBody = {
            type: ENTITY_IMPORT_TASK_TYPE_ENUM.SET_OTHER_VALIDATION_TABLE,
            ignoredRowIds,
            verifiedRowIds,
            doNotAllowNewEntry
        };
        return this._sendMsg(reqBody);
    };

    updateTableData = payload => {
        const reqBody = {
            type: ENTITY_IMPORT_TASK_TYPE_ENUM.UPDATE_TABLE_DATA,
            ...payload
        };
        return this._sendMsg(reqBody);
    };

    getEntitiesToUpload = () => {
        const reqBody = {
            type: ENTITY_IMPORT_TASK_TYPE_ENUM.GET_ENTITIES_TO_UPLOAD
        };
        return this._sendMsg(reqBody);
    };

    getErrorEntitiesDataForDownload = () => {
        const reqBody = {
            type:
                ENTITY_IMPORT_TASK_TYPE_ENUM.GET_ERROR_ENTITIES_DATA_FOR_DOWNLOAD
        };
        return this._sendMsg(reqBody);
    };

    getIgnoredEntitiesDataForDownload = () => {
        const reqBody = {
            type:
                ENTITY_IMPORT_TASK_TYPE_ENUM.GET_IGNORED_ENTITIES_ENTITIES_DATA_FOR_DOWNLOAD
        };
        return this._sendMsg(reqBody);
    };

    getReservedEntitiesDataForDownload = () => {
        const reqBody = {
            type:
                ENTITY_IMPORT_TASK_TYPE_ENUM.GET_RESERVED_ENTITIES_ENTITIES_DATA_FOR_DOWNLOAD
        };
        return this._sendMsg(reqBody);
    };

    reorderTableErrorsAndGetData = () => {
        const reqBody = {
            type: ENTITY_IMPORT_TASK_TYPE_ENUM.REORDER_TABLE_ERRORS
        };
        return this._sendMsg(reqBody);
    };

    getNextPage = payload => {
        const reqBody = {
            type: ENTITY_IMPORT_TASK_TYPE_ENUM.GET_NEXT_PAGE,
            ...payload
        };
        return this._sendMsg(reqBody);
    };

    resetImport = () => {
        const reqBody = {
            type: ENTITY_IMPORT_TASK_TYPE_ENUM.RESET_IMPORT
        };
        return this._sendMsg(reqBody);
    };

    retryFailedActionById = payload => {
        const reqBody = {
            type: ENTITY_IMPORT_TASK_TYPE_ENUM.RETRY_FAILED_ACTION,
            ...payload
        };
        return this._sendMsg(reqBody);
    };

    terminate = () => this.worker.terminate();
}

export default EntityImportWorkerController;
